import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAllRecentEvents } from '../marketsSlice';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  useTheme,
} from '@mui/material';
import { Timeline } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getEventIcon, getEventColor, getEventLabel } from '../../../utils/eventHelpers';
import { formattedSymbol, itemVariants } from './utils';

/**
 * RecentEventsWidget - Displays recent market events
 * 
 * @param {Object} config - Widget configuration
 * @param {string} [config.title] - Custom title (default: "Recent Events")
 * @param {number|string} [config.maxHeight] - Maximum height for widget
 */

const getEventAge = (eventDate) => {
  const eventTime = new Date(eventDate);
  const now = new Date();
  const diffTime = Math.abs(now - eventTime);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) {return '1 day ago';}
  if (diffDays < 7) {return `${diffDays} days ago`;}
  if (diffDays < 30) {return `${Math.floor(diffDays / 7)} weeks ago`;}
  return `${Math.floor(diffDays / 30)} months ago`;
};

const isSwingEvent = (eventType) => {
  return [
    'swing_high_formed',
    'swing_high_break',
    'swing_low_formed', 
    'swing_low_break',
    'swing_high_sweep',
    'swing_low_sweep',
    'higher_high',
    'higher_low',
    'lower_high',
    'lower_low'
  ].includes(eventType);
};

const RecentEventsWidget = ({ config = {} }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allRecentEvents, allRecentEventsStatus } = useSelector((state) => state.markets);
  const isLoading = allRecentEventsStatus === 'loading';
  const { settings: userSettings } = useSelector((state) => state.userSettings);
  
  const title = config.title || 'Recent Events';
  const maxHeight = config.maxHeight || '300px';

  // Fetch events on mount only (self-contained widget)
  useEffect(() => {
    if (!allRecentEvents || allRecentEvents.length === 0) {
      dispatch(fetchAllRecentEvents());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const eventNotifications = useMemo(() => {
    if (Array.isArray(userSettings?.eventNotifications)) {
      return userSettings.eventNotifications;
    }
    return null;
  }, [userSettings]);

  const filteredRecentEvents = useMemo(() => {
    if (!Array.isArray(allRecentEvents)) {
      return [];
    }
    const sanitized = allRecentEvents.filter((event) => event && event.symbol);
    if (!eventNotifications || eventNotifications.length === 0) {
      return sanitized;
    }
    const allowed = new Set(eventNotifications);
    return sanitized.filter((event) => allowed.has(event.eventType));
  }, [allRecentEvents, eventNotifications]);

  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ width: '100%', height: '400px', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Timeline sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                {title}
              </Typography>
            </Box>
            {!isLoading && (
              <Chip
                label={filteredRecentEvents.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Events List */}
          {isLoading ? (
            <List sx={{ px: 0 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <ListItem key={i} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: 'var(--dwlf-ink-06)' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Skeleton width="50%" sx={{ bgcolor: 'var(--dwlf-ink-06)' }} />}
                    secondary={<Skeleton width="70%" sx={{ bgcolor: 'var(--dwlf-ink-04)' }} />}
                  />
                </ListItem>
              ))}
            </List>
          ) : filteredRecentEvents.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ flexGrow: 1, gap: 2 }}
            >
              <Timeline sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {eventNotifications && eventNotifications.length === 0
                  ? 'Event notifications disabled'
                  : 'No events found'}
              </Typography>
              <Typography variant="caption" color="text.disabled" textAlign="center">
                {eventNotifications && eventNotifications.length === 0
                  ? 'Enable event types in settings to see them here'
                  : 'Market events will appear here when available'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ flexGrow: 1, maxHeight, overflow: 'auto', px: 0 }}>
              {filteredRecentEvents.map((event, index) => (
                <Link 
                  key={index} 
                  to={`/markets/${formattedSymbol(event.symbol)}`} 
                  style={{ textDecoration: 'none' }}
                >
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: (theme.vars || theme).palette.action.hover,
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ color: `${getEventColor(event.eventType, event)}.main`, fontSize: 20 }}>
                        {getEventIcon(event.eventType)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {event.symbol}
                          </Typography>
                          <Chip
                            label={getEventLabel(event)}
                            size="small"
                            color={getEventColor(event.eventType, event)}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '18px' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                              {event.date}
                            </Typography>
                            {event.timeframe && (
                              <Chip
                                label={event.timeframe.toUpperCase()}
                                size="small"
                                variant="outlined"
                                color="default"
                                sx={{ fontSize: '0.6rem', height: '16px' }}
                              />
                            )}
                          </Box>
                          {isSwingEvent(event.eventType) && (
                            <Chip
                              label={`Confirmed ${getEventAge(event.date)}`}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ fontSize: '0.6rem', height: '16px', width: 'fit-content' }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Link>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RecentEventsWidget;
